import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Per-customer feature flags. Read from orders.my_customer_features() — a
 * SECURITY DEFINER RPC that returns the `features` jsonb for the caller's
 * active customer.
 *
 * Default-ON semantics: a missing key (or no row at all) is treated as
 * enabled. Admins flip a flag to `false` in /admin to HIDE a section.
 * This keeps existing customers fully featured without a backfill.
 */

export type FeatureFlag = 'cylinder_inventory' | 'service_requests' | 'pay_online';

export interface CustomerFeatures {
  cylinderInventory: boolean;
  serviceRequests: boolean;
  payOnline: boolean;
}

const DEFAULTS: CustomerFeatures = {
  cylinderInventory: true,
  serviceRequests: true,
  payOnline: true,
};

export function useCustomerFeatures() {
  return useQuery<CustomerFeatures>({
    queryKey: ['customer-features'],
    queryFn: async (): Promise<CustomerFeatures> => {
      const { data, error } = await supabase
        .schema('orders')
        .rpc('my_customer_features');
      if (error || !data) return DEFAULTS;
      const f = (data ?? {}) as Record<string, boolean>;
      // Missing key => ON.
      return {
        cylinderInventory: f.cylinder_inventory !== false,
        serviceRequests: f.service_requests !== false,
        payOnline: f.pay_online !== false,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
